import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { FaSave, FaUpload, FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';

const OPCOES_TIPO = [
  { value: 'T', label: 'Terreno' },
  { value: 'C', label: 'Casa' },
  { value: 'A', label: 'Apartamento' },
];

const OPCOES_TIPO_VENDA = [
  { value: 'P', label: 'Próprios' },
  { value: 'T', label: 'Terceiros' },
  { value: 'A', label: 'Adm. Carteira' },
];

const OPCOES_TIPO_EMP = [
  { value: '', label: '(nenhum)' },
  { value: 'B', label: 'Bairro Planejado' },
  { value: 'L', label: 'Loteamento Fechado' },
  { value: 'V', label: 'Cond. Vertical' },
  { value: 'H', label: 'Cond. Horizontal' },
];

const OPCOES_SIM_NAO = [
  { value: 'S', label: 'Sim' },
  { value: 'N', label: 'Não' },
];

const OPCOES_COBRANCA = [
  { value: '', label: '(nenhum)' },
  { value: 'B', label: 'Bradesco' },
  { value: 'C', label: 'Caixa' },
  { value: 'I', label: 'Itaú' },
  { value: 'S', label: 'Santander' },
];

const OPCOES_REGRA_RES = [
  { value: '', label: '(nenhum)' },
  { value: 'U', label: 'Atualizado vr última parcela x qtde pagamentos' },
  { value: 'H', label: 'Atualizado vr histórico dos pagamentos' },
];

const OPCOES_TIPO_FRUICAO = [
  { value: '', label: '(nenhum)' },
  { value: 'V', label: 'Valor' },
  { value: 'P', label: 'Percentual' },
];

const OPCOES_TIPO_BASE_CALC = [
  { value: '0', label: 'V.V.' },
  { value: '1', label: 'V.V. - Corrigido pelo contrato p/ data 1ª correção' },
  { value: '2', label: 'V Prazo - Negócio de Contrato' },
  { value: '3', label: 'V Prazo - Corrigido pelo Contrato p/ data 1ª correção' },
  { value: '4', label: 'V Pago - Soma Totais parcelas pagas' },
  { value: '5', label: 'V Pago - Corrigido pelo Contrato p/ data 1ª correção' },
];

const OPCOES_DEB_AUT = [
  { value: 'N', label: 'Não' },
  { value: 'B', label: 'Bradesco' },
];

const OPCOES_TP_TAXA_TRANSF = [
  { value: '', label: '(nenhum)' },
  { value: 'F', label: 'Fixo' },
  { value: 'P', label: '% sobre valor do contrato' },
  { value: 'I', label: 'Quantidade de IGPM' },
];

const schema = Yup.object({
  codigo_cidade: Yup.number()
    .typeError('Cidade inválida')
    .required('Necessário informar Cidade')
    .min(1, 'Cidade inválida'),
  codigo_grupo_empresa: Yup.number()
    .typeError('Grupo de empresas inválido')
    .required('Grupo de empresas inválido')
    .min(1, 'Grupo inválido'),
  bol_juros: Yup.number().typeError('% Juros inválido').min(0, '% Juros inválido'),
  avulso: Yup.string().test('avulso-constraints', 'Empreendimento Avulso exige Gerencial = Não e Tipo Venda = Próprios', function (value) {
    if (value !== 'S') return true;
    const { gerencial, tipo_venda } = this.parent;
    return gerencial === 'N' && tipo_venda === 'P';
  }),
});

const findOpt = (opts, val) => {
  if (val == null || val === '') return null;
  const found = opts.find((o) => String(o.value) === String(val));
  return found != null ? found : null;
};

const SelectField = ({ name, options, value, disabled, onChange }) => (
  <Select
    options={options}
    value={findOpt(options, value)}
    onChange={(opt) => onChange(name, opt != null ? opt.value : '')}
    isDisabled={disabled}
    styles={customStyles}
    components={{ DropdownIndicator }}
    menuPortalTarget={document.body}
  />
);

const labelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 2 };
const sectionTitleStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: '#0747a6',
  borderBottom: '2px solid #0747a6',
  paddingBottom: 4,
  marginBottom: 12,
  marginTop: 8,
};

const FormDadosGerais = ({ dados, permissoes, familias, gruposEmpreendimento, razaoSocial, codigoFamilia, onSalvar, onRecarregar }) => {
  const primeiraRazao = razaoSocial != null && razaoSocial.length > 0 ? razaoSocial[0] : null;
  const disabledComercial = !permissoes.comercial;
  const disabledOperacional = !permissoes.operacional;
  const inputLogoRef = useRef(null);
  const inputLogoParceiroRef = useRef(null);
  const timeoutCidadeRef = useRef(null);
  const [cidades, setCidades] = useState([]);
  const [cidadesById, setCidadesById] = useState({});
  const [contasOptions, setContasOptions] = useState([]);
  const [sequenciasOptions, setSequenciasOptions] = useState([]);
  const [centrosCustoOptions, setCentrosCustoOptions] = useState([]);
  const [taxaDescricao, setTaxaDescricao] = useState('');
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    if (dados != null && dados.codigo_grupo_empresa) {
      api.listarContasLookupConfig(dados.codigo_grupo_empresa)
        .then((res) => {
          const lista = res && res.data ? res.data : (res || []);
          const opts = (Array.isArray(lista) ? lista : []).map((c) => ({
            value: c.codigo,
            label: c.codigo + ' - ' + c.descricao,
          }));
          setContasOptions(opts);
        })
        .catch(() => {});
    }
  }, [dados != null ? dados.codigo_grupo_empresa : null]);

  useEffect(() => {
    if (codigoFamilia) {
      api.listarSequenciasComissaoLookupConfig(codigoFamilia)
        .then((res) => {
          const lista = res && res.data ? res.data : (res || []);
          const opts = (Array.isArray(lista) ? lista : []).map((c) => ({
            value: c.codigo,
            label: c.codigo + ' - ' + c.descricao,
          }));
          setSequenciasOptions(opts);
        })
        .catch(() => {});
    }
  }, [codigoFamilia]);

  useEffect(() => {
    api.listarCentrosCustoConfig()
      .then((res) => {
        const lista = res && res.data ? res.data : (res || []);
        const opts = (Array.isArray(lista) ? lista : []).map((c) => ({
          value: c.codigo,
          label: c.codigo + ' - ' + c.descricao,
        }));
        setCentrosCustoOptions(opts);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (codigoFamilia) {
      api.buscarTaxaDescricaoConfig(codigoFamilia)
        .then((res) => {
          const d = res && res.data ? res.data : (res || {});
          setTaxaDescricao(d.descricao || '');
        })
        .catch(() => { setTaxaDescricao(''); });
    }
  }, [codigoFamilia]);

  const handleExportarPdfs = () => {
    if (!window.confirm('Exportar PDFs para este empreendimento?')) return;
    setExportando(true);
    api.exportarPdfsConfig({ codigo_familia: codigoFamilia })
      .then(() => { toast.success('PDFs exportados com sucesso!'); })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao exportar PDFs';
        toast.error(msg);
      })
      .finally(() => { setExportando(false); });
  };

  useEffect(() => {
    api.getCities().then((res) => {
      const lista = Array.isArray(res) ? res : [];
      setCidades(lista);
      const byId = {};
      lista.forEach((c) => { byId[c.codigo] = c; });
      setCidadesById(byId);
    }).catch(() => {});
  }, []);

  const removerAcentos = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const filtroCidades = useCallback((inputValue) => {
    const termo = removerAcentos(inputValue.toLowerCase());
    return cidades
      .filter((c) => removerAcentos(c.cidade.toLowerCase()).indexOf(termo) >= 0)
      .map((c) => ({ value: c.codigo, label: c.cidade }));
  }, [cidades]);

  const carregarCidadesAsync = (inputValue, callback) => {
    if (timeoutCidadeRef.current) clearTimeout(timeoutCidadeRef.current);
    timeoutCidadeRef.current = setTimeout(() => {
      callback(filtroCidades(inputValue));
    }, 300);
  };

  const getCidadeValue = (codigoCidade) => {
    if (codigoCidade == null || codigoCidade === '') return null;
    const found = cidadesById[codigoCidade];
    if (found) return { value: found.codigo, label: found.cidade };
    return { value: codigoCidade, label: `Cidade ${codigoCidade}` };
  };

  const handleUploadLogo = (file, tipo) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      const salvar = tipo === 'logo' ? api.salvarLogoEmpreendimento : api.salvarLogoParceiroEmpreendimento;
      salvar(codigoFamilia, base64)
        .then(() => {
          toast.success('Logo salva com sucesso!');
          if (onRecarregar) onRecarregar();
        })
        .catch((err) => {
          const msg = err && err.message ? err.message : 'Erro ao salvar logo';
          toast.error(msg);
        });
    };
    reader.readAsDataURL(file);
  };

  const initialValues = {
    codigo_familia: dados != null ? dados.codigo_familia : '',
    tipo: dados != null && dados.tipo != null ? dados.tipo : '',
    tipo_venda: dados != null && dados.tipo_venda != null ? dados.tipo_venda : '',
    tipo_emp: dados != null && dados.tipo_emp != null ? dados.tipo_emp : '',
    grupo: dados != null && dados.grupo != null ? dados.grupo : '',
    perc_obra: dados != null && dados.perc_obra != null ? dados.perc_obra : '',
    declara: dados != null && dados.declara != null ? dados.declara : 'N',
    gerencial: dados != null && dados.gerencial != null ? dados.gerencial : 'N',
    codigo_grupo_empresa:
      dados != null && dados.codigo_grupo_empresa != null ? dados.codigo_grupo_empresa : '',
    codigo_cidade: dados != null && dados.codigo_cidade != null ? dados.codigo_cidade : '',
    endereco: dados != null && dados.endereco != null ? dados.endereco : '',
    bairro: dados != null && dados.bairro != null ? dados.bairro : '',
    cep_dimob: dados != null && dados.cep_dimob != null ? dados.cep_dimob : '',
    serasa: dados != null && dados.serasa != null ? dados.serasa : 'N',
    dias_juridico: dados != null && dados.dias_juridico != null ? dados.dias_juridico : '',
    dias_tolerancia:
      dados != null && dados.dias_tolerancia != null ? dados.dias_tolerancia : '',
    cis: dados != null && dados.cis != null ? dados.cis : '',
    carencia_juros: dados != null && dados.carencia_juros != null ? dados.carencia_juros : '',
    bol_juros: dados != null && dados.bol_juros != null ? dados.bol_juros : '',
    bol_multa: dados != null && dados.bol_multa != null ? dados.bol_multa : '',
    declara_parc: dados != null && dados.declara_parc != null ? dados.declara_parc : '',
    avulso: dados != null && dados.avulso != null ? dados.avulso : 'N',
    controla_proposta:
      dados != null && dados.controla_proposta != null ? dados.controla_proposta : 'N',
    logo_contrato:
      dados != null && dados.logo_contrato != null ? dados.logo_contrato : 'N',
    cobranca: dados != null && dados.cobranca != null ? dados.cobranca : 'N',
    bol_pagar: dados != null && dados.bol_pagar != null ? dados.bol_pagar : 'N',
    bol_msg_adicional:
      dados != null && dados.bol_msg_adicional != null ? dados.bol_msg_adicional : '',
    carteira: dados != null && dados.carteira != null ? dados.carteira : '',
    corretor_emp:
      dados != null && dados.corretor_emp != null ? dados.corretor_emp : '',
    comissao_emp:
      dados != null && dados.comissao_emp != null ? dados.comissao_emp : '',
    centro_comissao:
      dados != null && dados.centro_comissao != null ? dados.centro_comissao : '',
    f_comissao: dados != null && dados.f_comissao != null ? dados.f_comissao : '',
    parcelas_juridico:
      dados != null && dados.parcelas_juridico != null ? dados.parcelas_juridico : '',
    comi: dados != null && dados.comi != null ? dados.comi : '',
    descricao_cri: dados != null && dados.descricao_cri != null ? dados.descricao_cri : '',
    transitorio: dados != null && dados.transitorio != null ? dados.transitorio : '',
    cliente_empresa_op:
      dados != null && dados.cliente_empresa_op != null ? dados.cliente_empresa_op : '',
    agua_res: dados != null && dados.agua_res != null ? dados.agua_res : '',
    cond_res: dados != null && dados.cond_res != null ? dados.cond_res : '',
    tx_presente: dados != null && dados.tx_presente != null ? dados.tx_presente : '',
    email_rescisao:
      dados != null && dados.email_rescisao != null ? dados.email_rescisao : '',
    regra_res: dados != null && dados.regra_res != null ? dados.regra_res : '',
    sem_dessagio:
      dados != null && dados.sem_dessagio != null ? dados.sem_dessagio : 'N',
    prazo_quita: dados != null && dados.prazo_quita != null ? dados.prazo_quita : '',
    vl_fruicao: dados != null && dados.vl_fruicao != null ? dados.vl_fruicao : '',
    tipo_fruicao:
      dados != null && dados.tipo_fruicao != null ? dados.tipo_fruicao : '',
    tipo_base_calculo_fruicao:
      dados != null && dados.tipo_base_calculo_fruicao != null
        ? String(dados.tipo_base_calculo_fruicao)
        : '',
    taxa_transf: dados != null && dados.taxa_transf != null ? dados.taxa_transf : '',
    taxa_transf_quitado:
      dados != null && dados.taxa_transf_quitado != null ? dados.taxa_transf_quitado : '',
    tp_taxa_transf:
      dados != null && dados.tp_taxa_transf != null ? dados.tp_taxa_transf : '',
    contabil_tx_transf:
      dados != null && dados.contabil_tx_transf != null ? dados.contabil_tx_transf : 'N',
    portador_adimplente:
      dados != null && dados.portador_adimplente != null ? dados.portador_adimplente : '',
    portador_construtor:
      dados != null && dados.portador_construtor != null ? dados.portador_construtor : '',
    carteira_sem_registro:
      dados != null && dados.carteira_sem_registro != null ? dados.carteira_sem_registro : '',
    carencia_correcao:
      dados != null && dados.carencia_correcao != null ? dados.carencia_correcao : 'N',
    dt_min_correcao:
      dados != null && dados.dt_min_correcao != null ? dados.dt_min_correcao : '',
    dt_min_juros:
      dados != null && dados.dt_min_juros != null ? dados.dt_min_juros : '',
    comissao_minima_parceria:
      dados != null && dados.comissao_minima_parceria != null
        ? dados.comissao_minima_parceria
        : '',
    averbacao: dados != null && dados.averbacao != null ? dados.averbacao : 'N',
    negativa: dados != null && dados.negativa != null ? dados.negativa : 'N',
    residuo: dados != null && dados.residuo != null ? dados.residuo : 'N',
    bx_residuo: dados != null && dados.bx_residuo != null ? dados.bx_residuo : 'N',
    deb_aut: dados != null && dados.deb_aut != null ? dados.deb_aut : 'N',
    minuta: dados != null && dados.minuta != null ? dados.minuta : 'N',
    m1: dados != null && dados.m1 != null ? dados.m1 : '',
    m2: dados != null && dados.m2 != null ? dados.m2 : '',
    m3: dados != null && dados.m3 != null ? dados.m3 : '',
    m4: dados != null && dados.m4 != null ? dados.m4 : '',
    texto_boleto: dados != null && dados.texto_boleto != null ? dados.texto_boleto : '',
    msg_cad_rapido:
      dados != null && dados.msg_cad_rapido != null ? dados.msg_cad_rapido : '',
    faixa_ini: dados != null && dados.faixa_ini != null ? dados.faixa_ini : '',
    faixa_fim: dados != null && dados.faixa_fim != null ? dados.faixa_fim : '',
    info_ma: dados != null && dados.info_ma != null ? dados.info_ma : '',
    transf_comissao_fundo: dados != null && dados.transf_comissao_fundo != null ? dados.transf_comissao_fundo : '',
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      enableReinitialize
      onSubmit={onSalvar}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
        <FormikForm>
          {/* Seção Comercial */}
          <div className="mb-4">
            <div style={sectionTitleStyle}>Comercial</div>
            <Row>
              <Col md={3} className="mb-3">
                <Form.Label style={labelStyle}>Tipo</Form.Label>
                <SelectField
                  name="tipo"
                  options={OPCOES_TIPO}
                  value={values.tipo}
                  disabled={disabledComercial}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label style={labelStyle}>Comercialização</Form.Label>
                <SelectField
                  name="tipo_venda"
                  options={OPCOES_TIPO_VENDA}
                  value={values.tipo_venda}
                  disabled={disabledComercial}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label style={labelStyle}>Grupo</Form.Label>
                <Select
                  options={gruposEmpreendimento}
                  value={gruposEmpreendimento.find((g) => g.value === values.grupo) || null}
                  onChange={(opt) => setFieldValue('grupo', opt != null ? opt.value : '')}
                  isDisabled={disabledComercial}
                  isClearable
                  placeholder="Selecione..."
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  menuPortalTarget={document.body}
                />
              </Col>
            </Row>
            <Row>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>% Obra</Form.Label>
                <Form.Control
                  type="number"
                  name="perc_obra"
                  value={values.perc_obra}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={disabledComercial}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Controla Proposta</Form.Label>
                <SelectField
                  name="controla_proposta"
                  options={OPCOES_SIM_NAO}
                  value={values.controla_proposta}
                  disabled={disabledComercial}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Logo no Contrato</Form.Label>
                <SelectField
                  name="logo_contrato"
                  options={OPCOES_SIM_NAO}
                  value={values.logo_contrato}
                  disabled={disabledComercial}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label style={labelStyle}>Logo</Form.Label>
                <input
                  ref={inputLogoRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleUploadLogo(e.target.files[0], 'logo')}
                />
                <div
                  style={{
                    width: '100%',
                    height: 80,
                    border: '1px dashed #dee2e6',
                    borderRadius: 4,
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#adb5bd',
                    fontSize: 12,
                    position: 'relative',
                  }}
                >
                  {dados != null && dados.logo_base64 ? (
                    <img src={`data:image/png;base64,${dados.logo_base64}`} alt="Logo" style={{ maxHeight: 72, maxWidth: '100%' }} />
                  ) : (
                    'Sem logo'
                  )}
                </div>
                {permissoes.comercial && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mt-1"
                    onClick={() => inputLogoRef.current.click()}
                  >
                    <FaUpload className="mr-1" /> Enviar
                  </Button>
                )}
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label style={labelStyle}>Logo Parceiro</Form.Label>
                <input
                  ref={inputLogoParceiroRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleUploadLogo(e.target.files[0], 'logo_parceiro')}
                />
                <div
                  style={{
                    width: '100%',
                    height: 80,
                    border: '1px dashed #dee2e6',
                    borderRadius: 4,
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#adb5bd',
                    fontSize: 12,
                    position: 'relative',
                  }}
                >
                  {dados != null && dados.logo_parceiro_base64 ? (
                    <img src={`data:image/png;base64,${dados.logo_parceiro_base64}`} alt="Logo Parceiro" style={{ maxHeight: 72, maxWidth: '100%' }} />
                  ) : (
                    'Sem logo'
                  )}
                </div>
                {permissoes.comercial && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mt-1"
                    onClick={() => inputLogoParceiroRef.current.click()}
                  >
                    <FaUpload className="mr-1" /> Enviar
                  </Button>
                )}
              </Col>
            </Row>
            <Row>
              <Col md={4} className="mb-3">
                <fieldset
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '10px 14px',
                  }}
                >
                  <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                    Declaração de Transferência
                  </legend>
                  <Row>
                    <Col md={6}>
                      <Form.Label style={labelStyle}>Imprime</Form.Label>
                      <SelectField
                        name="declara"
                        options={OPCOES_SIM_NAO}
                        value={values.declara}
                        disabled={disabledComercial}
                        onChange={setFieldValue}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label style={labelStyle}>A partir de</Form.Label>
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          name="declara_parc"
                          value={values.declara_parc}
                          onChange={handleChange}
                          disabled={disabledComercial}
                          style={{ width: '80px' }}
                        />
                        <span className="ml-2" style={{ fontSize: 12 }}>parcelas</span>
                      </div>
                    </Col>
                  </Row>
                </fieldset>
              </Col>
            </Row>
            {primeiraRazao != null && (
              <fieldset
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '10px 14px',
                  marginTop: '4px',
                }}
              >
                <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                  Razão
                </legend>
                <Row className="align-items-center">
                  <Col md={1}>
                    <Form.Label style={{ ...labelStyle, color: '#6c757d' }}>Operacional</Form.Label>
                  </Col>
                  <Col md={1}>
                    <Form.Control
                      value={primeiraRazao.codigo != null ? primeiraRazao.codigo : ''}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      value={primeiraRazao.operacional != null ? primeiraRazao.operacional : ''}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                </Row>
                <Row className="align-items-center mt-2">
                  <Col md={1}>
                    <Form.Label style={{ ...labelStyle, color: '#6c757d' }}>Contábil</Form.Label>
                  </Col>
                  <Col md={1}>
                    <Form.Control
                      value={primeiraRazao.contabil != null ? primeiraRazao.contabil : ''}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      value={primeiraRazao.contabil_descricao != null ? primeiraRazao.contabil_descricao : ''}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                </Row>
              </fieldset>
            )}
            <Row className="mt-3">
              <Col md={8} className="mb-3">
                <Form.Label style={labelStyle}>Info MA</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="info_ma"
                  value={values.info_ma}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={disabledComercial}
                  placeholder="Informações MA..."
                />
              </Col>
            </Row>
          </div>

          {/* Seção Operacional */}
          <div className="mb-4">
            <div style={sectionTitleStyle}>Operacional</div>
            <Row>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>
                  Grupo Empresa <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  name="codigo_grupo_empresa"
                  value={values.codigo_grupo_empresa}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={disabledOperacional}
                />
                {touched.codigo_grupo_empresa && errors.codigo_grupo_empresa && (
                  <div className="text-danger" style={{ fontSize: 12 }}>{errors.codigo_grupo_empresa}</div>
                )}
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label style={labelStyle}>
                  Cidade <span className="text-danger">*</span>
                </Form.Label>
                <AsyncSelect
                  loadOptions={carregarCidadesAsync}
                  defaultOptions={cidades.slice(0, 30).map((c) => ({ value: c.codigo, label: c.cidade }))}
                  onChange={(option) => setFieldValue('codigo_cidade', option ? option.value : null)}
                  value={getCidadeValue(values.codigo_cidade)}
                  isClearable
                  isDisabled={disabledOperacional}
                  placeholder="Pesquisar cidade..."
                  noOptionsMessage={() => 'Digite para buscar...'}
                  loadingMessage={() => 'Carregando...'}
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                  menuPortalTarget={document.body}
                />
                {touched.codigo_cidade && errors.codigo_cidade && (
                  <div className="text-danger" style={{ fontSize: 12 }}>{errors.codigo_cidade}</div>
                )}
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label style={labelStyle}>Endereço</Form.Label>
                <Form.Control
                  name="endereco"
                  value={values.endereco}
                  onChange={handleChange}
                  disabled={disabledOperacional}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Bairro</Form.Label>
                <Form.Control
                  name="bairro"
                  value={values.bairro}
                  onChange={handleChange}
                  disabled={disabledOperacional}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>CEP</Form.Label>
                <Form.Control
                  name="cep_dimob"
                  value={values.cep_dimob}
                  onChange={handleChange}
                  disabled={disabledOperacional}
                />
              </Col>
            </Row>
            <Row>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Tipo Empreendimento</Form.Label>
                <SelectField
                  name="tipo_emp"
                  options={OPCOES_TIPO_EMP}
                  value={values.tipo_emp}
                  disabled={disabledOperacional}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Gerencial</Form.Label>
                <SelectField
                  name="gerencial"
                  options={OPCOES_SIM_NAO}
                  value={values.gerencial}
                  disabled={disabledOperacional}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Avulso</Form.Label>
                <SelectField
                  name="avulso"
                  options={OPCOES_SIM_NAO}
                  value={values.avulso}
                  disabled={disabledOperacional}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Serasa</Form.Label>
                <SelectField
                  name="serasa"
                  options={OPCOES_SIM_NAO}
                  value={values.serasa}
                  disabled={disabledOperacional}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Transitório</Form.Label>
                <Select
                  options={contasOptions}
                  value={findOpt(contasOptions, values.transitorio)}
                  onChange={(opt) => setFieldValue('transitorio', opt != null ? opt.value : '')}
                  isDisabled={disabledOperacional}
                  isClearable
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  menuPortalTarget={document.body}
                  placeholder="Selecione..."
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Cliente Empresa Op.</Form.Label>
                <Select
                  options={contasOptions}
                  value={findOpt(contasOptions, values.cliente_empresa_op)}
                  onChange={(opt) => setFieldValue('cliente_empresa_op', opt != null ? opt.value : '')}
                  isDisabled={disabledOperacional}
                  isClearable
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  menuPortalTarget={document.body}
                  placeholder="Selecione..."
                />
              </Col>
            </Row>
            <Row>
              <Col md={4} className="mb-3">
                <Form.Label style={labelStyle}>Descrição CRI</Form.Label>
                <Form.Control
                  name="descricao_cri"
                  value={values.descricao_cri}
                  onChange={handleChange}
                  disabled={disabledOperacional}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Dias Jurídico</Form.Label>
                <Form.Control
                  type="number"
                  name="dias_juridico"
                  value={values.dias_juridico}
                  onChange={handleChange}
                  disabled={disabledOperacional}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Dias Tolerância</Form.Label>
                <Form.Control
                  type="number"
                  name="dias_tolerancia"
                  value={values.dias_tolerancia}
                  onChange={handleChange}
                  disabled={disabledOperacional || !permissoes.dias_tolerancia}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>CIS</Form.Label>
                <SelectField
                  name="cis"
                  options={OPCOES_SIM_NAO}
                  value={values.cis}
                  disabled={disabledOperacional}
                  onChange={setFieldValue}
                />
              </Col>
              <Col md={2} className="mb-3">
                <Form.Label style={labelStyle}>Parcelas Jurídico</Form.Label>
                <Form.Control
                  type="number"
                  name="parcelas_juridico"
                  value={values.parcelas_juridico}
                  onChange={handleChange}
                  disabled={disabledOperacional}
                />
              </Col>
            </Row>

            {/* Sub-seção Boleto */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Boleto
              </legend>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>% Juros</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="bol_juros"
                    value={values.bol_juros}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={disabledOperacional}
                    isInvalid={touched.bol_juros && !!errors.bol_juros}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.bol_juros}
                  </Form.Control.Feedback>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>% Multa</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="bol_multa"
                    value={values.bol_multa}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Carência Juros</Form.Label>
                  <Form.Control
                    type="number"
                    name="carencia_juros"
                    value={values.carencia_juros}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Bol. Pagar</Form.Label>
                  <Form.Control
                    name="bol_pagar"
                    value={values.bol_pagar}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Cobrança</Form.Label>
                  <SelectField
                    name="cobranca"
                    options={OPCOES_COBRANCA}
                    value={values.cobranca}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Carteira</Form.Label>
                  <Select
                    options={contasOptions}
                    value={findOpt(contasOptions, values.carteira)}
                    onChange={(opt) => setFieldValue('carteira', opt != null ? opt.value : '')}
                    isDisabled={disabledOperacional}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    placeholder="Selecione..."
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label style={labelStyle}>Msg. Adicional Boleto</Form.Label>
                  <Form.Control
                    name="bol_msg_adicional"
                    value={values.bol_msg_adicional}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Negativa</Form.Label>
                  <SelectField
                    name="negativa"
                    options={OPCOES_SIM_NAO}
                    value={values.negativa}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Resíduo</Form.Label>
                  <SelectField
                    name="residuo"
                    options={OPCOES_SIM_NAO}
                    value={values.residuo}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>BX Resíduo</Form.Label>
                  <SelectField
                    name="bx_residuo"
                    options={OPCOES_SIM_NAO}
                    value={values.bx_residuo}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Déb. Aut.</Form.Label>
                  <SelectField
                    name="deb_aut"
                    options={OPCOES_DEB_AUT}
                    value={values.deb_aut}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Minuta</Form.Label>
                  <SelectField
                    name="minuta"
                    options={OPCOES_SIM_NAO}
                    value={values.minuta}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Msg. Cobrança */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Msg. Cobrança
              </legend>
              <Row>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Linha 1</Form.Label>
                  <Form.Control
                    name="m1"
                    value={values.m1}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                    maxLength={80}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Linha 2</Form.Label>
                  <Form.Control
                    name="m2"
                    value={values.m2}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                    maxLength={80}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Linha 3</Form.Label>
                  <Form.Control
                    name="m3"
                    value={values.m3}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                    maxLength={80}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Linha 4</Form.Label>
                  <Form.Control
                    name="m4"
                    value={values.m4}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                    maxLength={80}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <Form.Label style={labelStyle}>Mensagem do Boleto</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="texto_boleto"
                    value={values.texto_boleto}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <Form.Label style={labelStyle}>Msg. Cadastro Rápido</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="msg_cad_rapido"
                    value={values.msg_cad_rapido}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Comissão */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Comissão
              </legend>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>% Comissão</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="comi"
                    value={values.comi}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Corretor Emp.</Form.Label>
                  <Select
                    options={contasOptions}
                    value={findOpt(contasOptions, values.corretor_emp)}
                    onChange={(opt) => setFieldValue('corretor_emp', opt != null ? opt.value : '')}
                    isDisabled={disabledOperacional}
                    isClearable
                    placeholder="Selecione..."
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Comissão Emp.</Form.Label>
                  <Select
                    options={sequenciasOptions}
                    value={findOpt(sequenciasOptions, values.comissao_emp)}
                    onChange={(opt) => setFieldValue('comissao_emp', opt != null ? opt.value : '')}
                    isDisabled={disabledOperacional}
                    isClearable
                    placeholder="Selecione..."
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Centro Comissão</Form.Label>
                  <Select
                    options={centrosCustoOptions}
                    value={findOpt(centrosCustoOptions, values.centro_comissao)}
                    onChange={(opt) => setFieldValue('centro_comissao', opt != null ? opt.value : '')}
                    isDisabled={disabledOperacional}
                    isClearable
                    placeholder="Selecione..."
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>F. Comissão</Form.Label>
                  <Form.Control
                    type="number"
                    name="f_comissao"
                    value={values.f_comissao}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Com. Mín. Parceria</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="comissao_minima_parceria"
                    value={values.comissao_minima_parceria}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>% Fundo Res.</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="transf_comissao_fundo"
                    value={values.transf_comissao_fundo}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Parâmetros de Rescisão */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Parâmetros de Rescisão
              </legend>
              <Row>
                <Col md={4} className="mb-3">
                  <Form.Label style={labelStyle}>Valor Total Pago</Form.Label>
                  <SelectField
                    name="regra_res"
                    options={OPCOES_REGRA_RES}
                    value={values.regra_res}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Sem Deságio</Form.Label>
                  <SelectField
                    name="sem_dessagio"
                    options={OPCOES_SIM_NAO}
                    value={values.sem_dessagio}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Prazo Quitação</Form.Label>
                  <Form.Control
                    type="number"
                    name="prazo_quita"
                    value={values.prazo_quita}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Averbação</Form.Label>
                  <SelectField
                    name="averbacao"
                    options={OPCOES_SIM_NAO}
                    value={values.averbacao}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Água RES</Form.Label>
                  <Form.Check
                    type="checkbox"
                    label="Sim"
                    checked={values.agua_res === 'S'}
                    onChange={(e) => setFieldValue('agua_res', e.target.checked ? 'S' : 'N')}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Cond. RES</Form.Label>
                  <Form.Check
                    type="checkbox"
                    label="Sim"
                    checked={values.cond_res === 'S'}
                    onChange={(e) => setFieldValue('cond_res', e.target.checked ? 'S' : 'N')}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Tx. Presente</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="tx_presente"
                    value={values.tx_presente}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={4} className="mb-3">
                  <Form.Label style={labelStyle}>E-mail Rescisão</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="email_rescisao"
                    value={values.email_rescisao}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Fruição */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Fruição
              </legend>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Valor Fruição</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="vl_fruicao"
                    value={values.vl_fruicao}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Tipo Fruição</Form.Label>
                  <SelectField
                    name="tipo_fruicao"
                    options={OPCOES_TIPO_FRUICAO}
                    value={values.tipo_fruicao}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={4} className="mb-3">
                  <Form.Label style={labelStyle}>Base Cálculo</Form.Label>
                  <SelectField
                    name="tipo_base_calculo_fruicao"
                    options={OPCOES_TIPO_BASE_CALC}
                    value={values.tipo_base_calculo_fruicao}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Taxa de Transferência */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Taxa de Transferência
              </legend>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Taxa Transf.</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="taxa_transf"
                    value={values.taxa_transf}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Taxa Transf. Quit.</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="taxa_transf_quitado"
                    value={values.taxa_transf_quitado}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Tipo Taxa Transf.</Form.Label>
                  <SelectField
                    name="tp_taxa_transf"
                    options={OPCOES_TP_TAXA_TRANSF}
                    value={values.tp_taxa_transf}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Cód. Contábil Tx Transf.</Form.Label>
                  <Form.Control
                    name="contabil_tx_transf"
                    value={values.contabil_tx_transf}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Taxa (Descrição)</Form.Label>
                  <Form.Control
                    value={taxaDescricao}
                    disabled
                    plaintext={false}
                    readOnly
                    style={{ backgroundColor: '#e9ecef' }}
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Portador / Carteira */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Portador / Carteira
              </legend>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Portador Adimplente</Form.Label>
                  <Select
                    options={contasOptions}
                    value={findOpt(contasOptions, values.portador_adimplente)}
                    onChange={(opt) => setFieldValue('portador_adimplente', opt != null ? opt.value : '')}
                    isDisabled={disabledOperacional}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    placeholder="Selecione..."
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Portador Construtor</Form.Label>
                  <Select
                    options={contasOptions}
                    value={findOpt(contasOptions, values.portador_construtor)}
                    onChange={(opt) => setFieldValue('portador_construtor', opt != null ? opt.value : '')}
                    isDisabled={disabledOperacional}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    placeholder="Selecione..."
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Carteira s/ Registro</Form.Label>
                  <Select
                    options={contasOptions}
                    value={findOpt(contasOptions, values.carteira_sem_registro)}
                    onChange={(opt) => setFieldValue('carteira_sem_registro', opt != null ? opt.value : '')}
                    isDisabled={disabledOperacional}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    placeholder="Selecione..."
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Faixa de Cliente */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Faixa de Cliente
              </legend>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Faixa Inicial</Form.Label>
                  <Form.Control
                    type="number"
                    name="faixa_ini"
                    value={values.faixa_ini}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Faixa Final</Form.Label>
                  <Form.Control
                    type="number"
                    name="faixa_fim"
                    value={values.faixa_fim}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
            </fieldset>

            {/* Sub-seção Exportar PDFs */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Exportar Documentos
              </legend>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportarPdfs}
                disabled={exportando || disabledOperacional}
              >
                <FaFilePdf className="mr-1" /> Exportar PDFs
              </Button>
            </fieldset>

            {/* Sub-seção Carência / Correção */}
            <fieldset
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              <legend style={{ width: 'auto', fontSize: 12, fontWeight: 700, color: '#0747a6', marginBottom: 6 }}>
                Carência / Correção
              </legend>
              <Row>
                <Col md={2} className="mb-3">
                  <Form.Label style={labelStyle}>Carência Correção</Form.Label>
                  <SelectField
                    name="carencia_correcao"
                    options={OPCOES_SIM_NAO}
                    value={values.carencia_correcao}
                    disabled={disabledOperacional}
                    onChange={setFieldValue}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Dt. Mín. Correção</Form.Label>
                  <Form.Control
                    type="date"
                    name="dt_min_correcao"
                    value={values.dt_min_correcao}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label style={labelStyle}>Dt. Mín. Juros</Form.Label>
                  <Form.Control
                    type="date"
                    name="dt_min_juros"
                    value={values.dt_min_juros}
                    onChange={handleChange}
                    disabled={disabledOperacional}
                  />
                </Col>
              </Row>
            </fieldset>
          </div>

          <div className="text-right mt-3">
            <Button type="submit" variant="success" size="sm">
              <FaSave className="mr-1" /> Salvar Dados Gerais
            </Button>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
};

export default FormDadosGerais;
